import { gql, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import React, { useState } from 'react';

import { useStorage } from '~Components/Storage';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import { useBridgeApi, useButton, useDeviceInfo, useDispatch, useI18n, useSelector } from '~Hooks';
import { Input, ModalContent } from '~Scenes/Handy/styles';
import { BlueButton, Box } from '~Styles';
import { addToast } from '~actions';
import { VenueTerminals, VenueTerminalsVariables } from './__queries__';

/** Tappable suggestion row for a terminal found on the venue */
const Terminal = styled(Box)`
  background: var(--colorBlueGray1100);
  border-radius: 4px;
`;

const TERMINALS_QUERY = gql`
  query VenueTerminals($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      adyenTerminals {
        id
        details {
          terminal
          ethernetIp
          wifiIp
        }
      }
    }
  }
`;

/** Handheld payment terminal setup: Zettle via the app bridge, Adyen by terminal ID */
const TerminalSetup = () => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId!);
  const appVersion = useSelector((state) => state.appVersion); // TEMP-ZETTLE-DISABLE
  const dispatch = useDispatch();
  const [{ adyenTerminals }, updateStorage] = useStorage();
  const idInputModal = useModal();
  const [terminalIdInput, setTerminalIdInput] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const hasAdyen = paymentMethods.some((method) => method.id === 'ADYEN');
  const hasZettle = paymentMethods.some((method) => method.id === 'IZETTLE');

  const terminals = useQuery<VenueTerminals, VenueTerminalsVariables>(TERMINALS_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      venueId,
    },
    skip: !venueId,
  });
  const allTerminals = terminals.data?.venue.adyenTerminals || [];
  const s1F2OrS1F4Terminals = allTerminals.filter(
    (terminal) => terminal.id.includes('S1F2-') || terminal.id.includes('S1F4Pro-'),
  );

  const deviceInfo = useDeviceInfo();
  const bridgeApi = useBridgeApi();

  const addAdyenTerminal = () => {
    if (!terminalIdInput) {
      return;
    }

    setLoading(true);

    // Create storage object
    const terminal = {
      POIID: terminalIdInput,
      // If it's an S1F2 or S1F4 Pro terminal, we assume it's a local terminal
      IP:
        terminalIdInput.includes('S1F2') || terminalIdInput.includes('S1F4Pro')
          ? '127.0.0.1'
          : null,
      hasPrinter: true,
    };

    // Wait 1 seconds before closing modal
    setTimeout(() => {
      // Add terminal to storage (and remove any existing terminals)
      updateStorage({
        adyenTerminals: [terminal],
      });
      idInputModal.toggle();
      setLoading(false);
      dispatch(addToast(i18n('Settings.Payments.TerminalConfigured'), true));
    }, 1000);
  };

  const zettleButton = useButton({
    onClick: () => window.postMessageBridge({ showIZettleSettings: true }),
  });

  const tipButton = useButton({
    onClick: () => bridgeApi.showZettleTipSettings(),
  });

  const setupAdyenButton = useButton({
    onClick: () => idInputModal.toggle(),
  });

  const addButton = useButton({
    onClick: () => addAdyenTerminal(),
    disabled: !terminalIdInput || loading,
  });

  /**
   * If we dont have device info (outdated client or slow response),
   * we show nothing instead of accidentally showing the wrong setup.
   */
  if (!deviceInfo) {
    return null;
  }

  // If not adyen, we assume it's zettle. Sometimes customers have 0 payment methods, and thats = zettle
  if (hasZettle || (!deviceInfo?.isAdyenTerminal && !hasAdyen)) {
    return (
      <>
        <Box marginTop="medium" marginBottom="medium">
          <Heading level={6} bottom={8} color="var(--colorWhite)">
            {i18n('Settings.Payments.Zettle')}
          </Heading>
          <BlueButton size="small" {...zettleButton}>
            {i18n('Settings.Payments.Open')}
          </BlueButton>
        </Box>
        {appVersion.charAt(0) !== '4' && ( // TEMP-ZETTLE-DISABLE
          <Box marginBottom="medium">
            <Heading level={6} bottom={8} color="var(--colorWhite)">
              {i18n('Settings.Payments.ZettleTip')}
            </Heading>
            <BlueButton size="small" {...tipButton}>
              {i18n('Settings.Payments.Open')}
            </BlueButton>
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Box marginTop="medium" marginBottom="medium">
        <Heading level={6} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.Payments.Adyen')}
        </Heading>
        <BlueButton size="small" {...setupAdyenButton}>
          {i18n('Settings.Payments.Configure')}
        </BlueButton>
      </Box>
      <Modal isShowing={idInputModal.isShowing} hide={idInputModal.toggle}>
        <ModalContent>
          <Heading level={3} color="var(--colorWhite)">
            {i18n('Settings.Payments.EnterTerminalId')}
          </Heading>
          <Paragraph color="var(--colorWhite)" level={5} top={8} bottom={8}>
            {i18n('Settings.Payments.EnterTerminalId.Text')}
          </Paragraph>
          <Box display="flex" alignItems="center" gap="xs" marginBottom="small">
            <Paragraph color="var(--colorWhite)" level={5} bottom={0} top={16}>
              {i18n('Settings.TerminalID')}:
            </Paragraph>
            <Input value={terminalIdInput} onChange={(e) => setTerminalIdInput(e.target.value)} />
          </Box>
          {(deviceInfo?.isAdyenTerminal ? s1F2OrS1F4Terminals : allTerminals).map((terminal) => (
            <Terminal
              key={terminal.id}
              padding="xs"
              marginBottom="xs"
              onClick={() => setTerminalIdInput(terminal.id)}>
              <Paragraph color="var(--colorWhite)" level={5} bottom={0}>
                {terminal.id}
              </Paragraph>
            </Terminal>
          ))}
          <BlueButton fluid {...addButton}>
            {loading ? <Loader size="xs" color="var(--colorWhite)" /> : i18n('Settings.Add')}
          </BlueButton>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TerminalSetup;
